import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { Card } from "./ui/card";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { ConfirmDialog } from "./ExpiredAccounts";
import { Plus, Pencil, Trash2, Gift } from "lucide-react";
import { PartnerForm } from "./PartnerForm";
import { AdminPartnerReview } from "./AdminPartnerReview";
import { brl } from "./PurchaseDialog";

const API_ROOT = process.env.REACT_APP_BACKEND_URL;

export const BenefitLogo = ({ url, name, className = "h-14 w-14" }) => (
  url
    ? <img src={`${API_ROOT}${url}`} alt={name} className={`${className} rounded-xl object-cover bg-white`} />
    : <div className={`${className} rounded-xl bg-muted flex items-center justify-center`}><Gift className="h-6 w-6 text-amber-400" /></div>
);

const BenefitRow = ({ b, onEdit, onRemove }) => (
  <div className="flex flex-col sm:flex-row sm:items-center gap-3 p-3 rounded-xl border border-border" data-testid={`admin-benefit-${b.id}`}>
    <BenefitLogo url={b.logo_url} name={b.name} className="h-12 w-12" />
    <div className="flex-1 min-w-0">
      <div className="font-medium flex items-center gap-2 flex-wrap">
        {b.name} <Badge variant="outline">{b.category}</Badge>
        {b.discount && <Badge className="gold-btn">{b.discount}</Badge>}
        {b.price && <Badge variant="outline" className="text-emerald-500 border-emerald-500/40">{b.original_price ? `De ${brl(b.original_price)} por ` : ""}{brl(b.price)}</Badge>}
        {b.owner_name && <Badge variant="outline">Parceiro: {b.owner_name}</Badge>}
        {b.status === "rejected" && <Badge variant="outline" className="text-rose-500">Rejeitado</Badge>}
        {!b.active && <Badge variant="outline">Oculto</Badge>}
      </div>
      <div className="text-xs text-muted-foreground truncate">{b.address ? `${b.address} · ` : ""}{b.description}</div>
    </div>
    <div className="flex gap-2 shrink-0">
      <Button size="sm" variant="outline" onClick={() => onEdit(b)} data-testid={`benefit-edit-${b.id}`}>
        <Pencil className="h-3 w-3 mr-1" /> Editar
      </Button>
      <Button size="sm" variant="outline" className="text-rose-500" onClick={() => onRemove(b)} data-testid={`benefit-delete-${b.id}`}>
        <Trash2 className="h-3 w-3 mr-1" /> Excluir
      </Button>
    </div>
  </div>
);

export const AdminBenefits = () => {
  const [list, setList] = useState(null);
  const [editing, setEditing] = useState(null);
  const [removing, setRemoving] = useState(null);

  const load = useCallback(() => {
    api.get("/admin/benefits").then((r) => setList(r.data)).catch((e) => toast.error(formatApiError(e)));
  }, []);
  useEffect(() => { load(); }, [load]);

  const remove = async () => {
    try {
      await api.delete(`/admin/benefits/${removing.id}`);
      toast.success("Excluído");
      setRemoving(null);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const published = (list || []).filter((b) => b.status !== "pending");

  return (
    <div className="space-y-6">
      <AdminPartnerReview onChanged={load} />
      <Card className="p-6" data-testid="admin-benefits">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
          <div>
            <div className="font-display text-lg font-bold">Benefícios e empresas parceiras</div>
            <p className="text-xs text-muted-foreground">Itens da vitrine. Cadastros de associados entram aqui depois de aprovados.</p>
          </div>
          <Button className="gold-btn" onClick={() => setEditing({})} data-testid="benefit-new-btn">
            <Plus className="h-4 w-4 mr-2" /> Novo produto / empresa
          </Button>
        </div>
        {!list ? <p className="text-sm text-muted-foreground">Carregando...</p>
          : published.length === 0 ? <p className="text-sm text-muted-foreground" data-testid="admin-benefits-empty">Nenhum item cadastrado.</p>
          : <div className="space-y-3">{published.map((b) => <BenefitRow key={b.id} b={b} onEdit={setEditing} onRemove={setRemoving} />)}</div>}
        <PartnerForm editing={editing} onClose={() => setEditing(null)} onSaved={load} base="/admin/benefits" admin />
        <ConfirmDialog open={!!removing} onOpenChange={(o) => !o && setRemoving(null)} testid="benefit-delete-dialog"
          title="Excluir item?" description={removing ? `${removing.name} será removido da vitrine.` : ""} onConfirm={remove} />
      </Card>
    </div>
  );
};
